import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Firestore } from '@angular/fire/firestore'
import { AFFILIATE_DEFAULTS, AffiliateAdminRow, AffiliateKind, AffiliateKindLabels, AffiliateReferralType, AffiliateStatus } from '@parkupp/core'
import { ToastrService } from 'ngx-toastr'
import { AffiliateAttributionView, AffiliateBuildingRow, AffiliateLandlordRow, AffiliateService } from '../../../services/affiliate.service'

interface AttributionTarget {
    kind: 'landlord' | 'building'
    merchantKey: string
    parkingKey?: string
    name: string
    existing: AffiliateAttributionView | null
    firstListedAt?: string | null
}

interface FoundUser {
    uid: string
    name: string
    email: string
}

@Component({
    selector: 'app-admin-affiliates',
    templateUrl: './admin-affiliates.component.html',
    styleUrls: ['./admin-affiliates.component.scss'],
})
export class AdminAffiliatesComponent implements OnInit {
    tab: 'affiliates' | 'landlords' = 'affiliates'
    loading = true
    rows: AffiliateAdminRow[] = []
    landlords: AffiliateLandlordRow[] = []
    landlordsLoaded = false
    landlordFilter: 'all' | 'attributed' | 'unattributed' = 'all'
    kindFilter: AffiliateKind | 'all' = 'all'
    search = ''
    kinds = Object.values(AffiliateKind)
    Kind = AffiliateKind

    // Create affiliate
    showCreate = false
    lookupEmail = ''
    lookingUp = false
    found: FoundUser | null = null
    saving = false
    form = this.blankForm()

    // Attribute a landlord introduction or a building introduction
    attributing: AttributionTarget | null = null
    attribution = { affiliateUid: '', notes: '', replace: false, listedAt: '' }

    defaults = AFFILIATE_DEFAULTS

    constructor(private affiliateService: AffiliateService, private firestore: Firestore, private toastr: ToastrService, private router: Router, private route: ActivatedRoute) {}

    ngOnInit(): void {
        if (this.route.snapshot.queryParamMap.get('tab') === 'landlords') this.setTab('landlords')
        this.load()
    }

    async load() {
        this.loading = true
        try {
            this.rows = await this.affiliateService.adminList()
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not load affiliates')
        } finally {
            this.loading = false
        }
    }

    async setTab(tab: 'affiliates' | 'landlords') {
        this.tab = tab
        this.router.navigate([], { queryParams: { tab: tab === 'affiliates' ? null : tab }, replaceUrl: true })
        if (tab === 'landlords' && !this.landlordsLoaded) await this.loadLandlords()
    }

    async loadLandlords() {
        try {
            this.landlords = await this.affiliateService.adminLandlords()
            this.landlordsLoaded = true
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not load portfolios')
        }
    }

    get filteredRows(): AffiliateAdminRow[] {
        const term = this.search.trim().toLowerCase()
        return this.rows
            .filter((r) => this.kindFilter === 'all' || (r.kind || AffiliateKind.PARKER_PROMOTER) === this.kindFilter)
            .filter((r) => !term || [r.displayName, r.email, r.code, r.phone].some((v) => (v || '').toLowerCase().includes(term)))
    }

    /** Portfolios matching the filter; buildings filtered the same way inside each portfolio. */
    get filteredLandlords(): AffiliateLandlordRow[] {
        const term = this.search.trim().toLowerCase()
        const credited = (b: AffiliateBuildingRow) => b.referral?.status === 'active'
        return this.landlords
            .map((l) => ({
                ...l,
                buildings: l.buildings.filter((b) => {
                    if (this.landlordFilter === 'attributed' && !credited(b)) return false
                    if (this.landlordFilter === 'unattributed' && credited(b)) return false
                    return true
                }),
            }))
            .filter((l) => {
                const introduced = l.introduction?.status === 'active'
                if (this.landlordFilter === 'attributed' && !introduced && !l.buildings.length) return false
                if (this.landlordFilter === 'unattributed' && !l.buildings.length && introduced) return false
                if (!term) return true
                return [l.companyName, l.introduction?.affiliateName, ...l.buildings.map((b) => b.title), ...l.buildings.map((b) => b.referral?.affiliateName)].some((v) =>
                    (v || '').toLowerCase().includes(term)
                )
            })
    }

    get activeAffiliates(): AffiliateAdminRow[] {
        // Guards are paid per parker, so they can't be credited with landlords or buildings
        return this.rows.filter((r) => r.status !== AffiliateStatus.TERMINATED && r.kind !== AffiliateKind.SECURITY_GUARD)
    }

    total(field: 'revenueAttributed' | 'pending' | 'approved' | 'requested' | 'paid'): number {
        return this.rows.reduce((sum, r) => sum + (field === 'revenueAttributed' ? r.revenueAttributed : r.balances[field]), 0)
    }

    get totalClicks(): number {
        return this.rows.reduce((sum, r) => sum + (r.stats?.uniqueClicks || 0), 0)
    }

    get totalReferrals(): number {
        return this.rows.reduce((sum, r) => sum + (r.stats?.parkerReferrals || 0) + (r.stats?.buildingReferrals || 0), 0)
    }

    get openRequests(): number {
        return this.rows.reduce((sum, r) => sum + r.openPayoutRequests, 0)
    }

    kindLabel(kind: AffiliateKind | undefined): string {
        return AffiliateKindLabels[kind || AffiliateKind.PARKER_PROMOTER]
    }

    get totalBountiesDue(): number {
        return this.rows.reduce((sum, r) => sum + (r.bountiesDue || 0), 0)
    }

    private blankForm() {
        return {
            kind: AffiliateKind.PARKER_PROMOTER as AffiliateKind,
            code: '',
            parkerCommissionPct: AFFILIATE_DEFAULTS.parkerCommissionPct,
            landlordCommissionPct: AFFILIATE_DEFAULTS.landlordCommissionPct,
            parkerWindowMonths: AFFILIATE_DEFAULTS.parkerWindowMonths,
            landlordWindowMonths: AFFILIATE_DEFAULTS.landlordWindowMonths,
            notes: '',
            // Security guards (no login)
            displayName: '',
            phone: '',
            guardEmail: '',
            bountyAmount: AFFILIATE_DEFAULTS.guardBountyAmount,
            bountyPaidInvoices: AFFILIATE_DEFAULTS.guardBountyPaidInvoices,
        }
    }

    get isGuardForm(): boolean {
        return this.form.kind === AffiliateKind.SECURITY_GUARD
    }

    get canCreate(): boolean {
        return this.isGuardForm ? !!this.form.displayName.trim() : !!this.found
    }

    open(row: AffiliateAdminRow) {
        this.router.navigate(['/admin/affiliates', row.uid])
    }

    // ---- Create ----
    openCreate() {
        this.showCreate = true
        this.found = null
        this.lookupEmail = ''
        this.form = this.blankForm()
    }

    async lookup() {
        const email = this.lookupEmail.trim()
        if (!email) return
        this.lookingUp = true
        this.found = null
        try {
            const match = await this.affiliateService.findAccountByEmail(email)
            if (!match) {
                this.toastr.warning('No ParkUpp user with that email. They need to sign up first.')
                return
            }
            if (this.rows.some((r) => r.uid === match.uid || (r.email || '').toLowerCase() === email.toLowerCase())) {
                this.toastr.info('That user is already an affiliate.')
                return
            }
            this.found = { uid: match.uid, name: match.name, email: match.email }
        } catch (error: any) {
            this.toastr.error(error?.message || 'Lookup failed')
        } finally {
            this.lookingUp = false
        }
    }

    async create() {
        if (!this.canCreate) return
        this.saving = true
        try {
            const f = this.form
            const result = this.isGuardForm
                ? // Security guard: no login, created on their behalf
                  await this.affiliateService.adminSave({
                      kind: AffiliateKind.SECURITY_GUARD,
                      displayName: f.displayName.trim(),
                      phone: f.phone.trim() || null,
                      guardEmail: f.guardEmail.trim() || null,
                      bountyAmount: f.bountyAmount,
                      bountyPaidInvoices: f.bountyPaidInvoices,
                      code: f.code.trim() || null,
                      notes: f.notes,
                      status: AffiliateStatus.ACTIVE,
                  })
                : // The email lets the server find the login account if the search landed on a CRM lead record
                  await this.affiliateService.adminSave({
                      uid: this.found!.uid,
                      email: this.found!.email,
                      kind: f.kind,
                      code: f.code.trim() || null,
                      parkerCommissionPct: f.parkerCommissionPct,
                      landlordCommissionPct: f.landlordCommissionPct,
                      parkerWindowMonths: f.parkerWindowMonths,
                      landlordWindowMonths: f.landlordWindowMonths,
                      notes: f.notes,
                      status: AffiliateStatus.ACTIVE,
                  })
            this.toastr.success(`Affiliate created with code ${result.code}`)
            this.showCreate = false
            await this.load()
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not create the affiliate')
        } finally {
            this.saving = false
        }
    }

    // ---- Landlord & building attribution ----
    openAttributeLandlord(row: AffiliateLandlordRow) {
        this.attributing = { kind: 'landlord', merchantKey: row.merchantKey, name: row.companyName, existing: row.introduction }
        this.attribution = { affiliateUid: '', notes: '', replace: row.introduction?.status === 'active', listedAt: '' }
    }

    openAttributeBuilding(row: AffiliateLandlordRow, building: AffiliateBuildingRow) {
        this.attributing = { kind: 'building', merchantKey: row.merchantKey, parkingKey: building.parkingKey, name: building.title, existing: building.referral, firstListedAt: building.firstListedAt }
        this.attribution = {
            affiliateUid: row.introduction?.status === 'active' ? row.introduction.affiliateUid : '',
            notes: '',
            replace: building.referral?.status === 'active',
            listedAt: (building.firstListedAt || '').slice(0, 10),
        }
    }

    async saveAttribution() {
        const target = this.attributing
        if (!target || !this.attribution.affiliateUid) return
        this.saving = true
        try {
            await this.affiliateService.adminAttachReferral({
                type: target.kind === 'building' ? AffiliateReferralType.BUILDING : AffiliateReferralType.LANDLORD,
                affiliateUid: this.attribution.affiliateUid,
                merchantKey: target.merchantKey,
                parkingKey: target.parkingKey,
                listedAt: target.kind === 'building' && this.attribution.listedAt ? new Date(this.attribution.listedAt).toISOString() : null,
                notes: this.attribution.notes,
                replace: this.attribution.replace,
            })
            this.toastr.success(`${target.name} attributed`)
            this.attributing = null
            await Promise.all([this.loadLandlords(), this.load()])
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not save the attribution')
        } finally {
            this.saving = false
        }
    }

    async revoke(name: string, referral: AffiliateAttributionView | null) {
        if (!referral) return
        const notes = prompt(`Remove the introduction for ${name}? Future payments will not earn a fee share. Reason:`)
        if (notes === null) return
        try {
            await this.affiliateService.adminRevokeReferral(referral.id, notes)
            this.toastr.success('Introduction removed')
            await Promise.all([this.loadLandlords(), this.load()])
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not remove the introduction')
        }
    }
}
